import React, { useState } from 'react';
import { useQuery } from 'react-query';

import { Form } from 'antd';
import styled from 'styled-components';

import { getAmenities, getAmenityCategories, updateCommunityAmenity } from 'apis/AmenitiesAPI';
import { useRootContext } from 'components/layout/RootContext';
import Checkbox from 'components/lib/Checkbox';

import { Amenity } from './types';
import Loader from '../../lib/Loader';
import ShopDataModalContentHeader from '../../ShopData/common/ShopDataModalContentHeader';
import { useEditDataModalContext } from '../common/edit/EditDataModalContext';
import CommunityInfoConfirmationModal from '../CommunityInfoConfirmationModal';
import { useCommunityInfoContext } from '../CommunityInfoContext';

const AmenitiesContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  padding: 9px 31px 9px;
  gap: 10px;
  max-width: 1000px;
  & > * {
    flex: 1 1 calc(25% - 10px);
    max-width: calc(25% - 10px);
  }
`;

const AmenityItem = styled.div`
  display: flex;
`;

type Props = {
  community?: any;
  disabled?: boolean;
};

const AmenitiesEdit = ({ community, disabled = false }: Props) => {
  const { showMessage } = useRootContext();
  const { communityInfo } = useCommunityInfoContext();
  const { form, modified, setModified, setLoading, onSuccess } = useEditDataModalContext();
  const [showConfirmation, setShowConfirmation] = useState(false);

  const {
    data: amenities,
    isLoading: isLoadingAmenities,
    refetch: refetchAmmenities,
  } = useQuery({
    queryKey: ['amenities', 'full', communityInfo?.id],
    queryFn: () => getAmenities(communityInfo?.id || community?.id),
    enabled: Boolean(communityInfo || community),
    refetchOnWindowFocus: false,
  });

  const {
    data: categories,
    isLoading: isLoadingCategories,
    refetch: refetchCategories,
  } = useQuery({
    queryKey: ['amenities', 'categories', communityInfo?.id || community?.id],
    queryFn: () => getAmenityCategories(),
    enabled: Boolean(communityInfo || community),
    refetchOnWindowFocus: false,
  });

  const handleConfirmSubmit = async () => {
    try {
      const values = await form?.validateFields();
      setLoading(true);

      const payload = Object.values(values);

      await updateCommunityAmenity(communityInfo?.id || community?.id, payload);

      showMessage('success', 'Community Amenities updated successfully.');
      refetchAmmenities();
      refetchCategories();
      onSuccess();
    } catch (error) {
      showMessage('error', 'Trouble updating community Amenities!');
    } finally {
      setLoading(false);
      setShowConfirmation(false);
    }
  };

  const handleValuesChanged = () => {
    if (!modified) {
      setModified(true);
    }
  };

  if (isLoadingAmenities || isLoadingCategories) {
    return <Loader active />;
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={() => setShowConfirmation(true)}
        onValuesChange={handleValuesChanged}
        disabled={disabled}
        initialValues={{
          ...amenities?.reduce((acc: any, item: Amenity) => {
            acc[`${item.id}`] = {
              value: item.checked,
              amenity: item.id,
              community: community?.id ?? communityInfo?.id,
              id: item.community_amenity_id,
            };
            return acc;
          }, {}),
        }}
      >
        {categories
          ?.filter((category: any) => amenities.find((amenity: any) => amenity.category_id === category.id))
          .map((category: any) => (
            <React.Fragment key={category.id}>
              <ShopDataModalContentHeader title={category.name} />
              <AmenitiesContainer>
                {amenities
                  .filter((amenity: any) => amenity.category_id === category.id)
                  .map((amenity: any) => (
                    <AmenityItem key={amenity.id}>
                      <Form.Item noStyle name={[`${amenity.id}`, 'id']} hidden />
                      <Form.Item noStyle name={[`${amenity.id}`, 'community']} hidden />
                      <Form.Item noStyle name={[`${amenity.id}`, 'amenity']} hidden />
                      <Form.Item noStyle name={[`${amenity.id}`, 'value']} valuePropName="checked" key={amenity.id}>
                        <Checkbox style={{ fontSize: 14 }} className={'green'}>
                          {amenity.name}
                        </Checkbox>
                      </Form.Item>
                    </AmenityItem>
                  ))}
              </AmenitiesContainer>
            </React.Fragment>
          ))}
      </Form>

      <CommunityInfoConfirmationModal
        showCommunityIncentivesConfirmationModal={showConfirmation}
        communityName={communityInfo?.comunity_name}
        onConfirm={handleConfirmSubmit}
        onClose={() => setShowConfirmation(false)}
      />
    </>
  );
};

export default AmenitiesEdit;
